// グループ宛通知書の登録番号（適格請求書発行事業者番号）の解決。
//
// 経緯（本城さん 2026-08-26）:
//   「AT-STYLE様の通知書にインボイス番号が表示されていない」
//   グループ宛通知書はマスタに行が無い合成ドライバー（store.tsx の group:<ruleId>）で作るため、
//   登録番号を持てず、インボイス値引きを抑止するためのダミー "GROUP" を入れていた。
//   通知書側（recipient.ts displayInvoiceNo）は T+13桁 の形だけを表示するので、
//   ダミーは表示されず「登録番号：…」の行ごと出ていなかった。
//
// 解決の順番:
//   ① 設定画面で入力された GroupRule.invoiceNo（T+13桁）
//   ② マスタで「所属」がグループ名と一致する行の登録番号（AT-STYLE様なら代表の淺田様の行に
//      ㈱AT-STYLE / T4010401083399 が入っている）。在籍者を優先する。
//      ただし**該当行に異なる番号が複数ある場合は自動採用しない**（所属欄が同じでも、
//      各人が自分の登録番号を持っている運用があり得る。適格請求書の番号を推測で載せない）
//   ③ どちらも無ければ null → 呼び出し側でダミー "GROUP"（従来どおり無表示・値引き抑止）
//
// ※ ダミーを空文字にしてはいけない。空にすると common.ts の finalizeStatement が
//    「インボイス未登録」と判定し、グループ宛通知書に 2% 値引き行が生えて金額が変わる。

import type { Driver, GroupRule } from "../../types";
import { isRetiredDriver } from "../driverStatus";

/** インボイス値引きを抑止するためのダミー値（表示はされない） */
export const GROUP_INVOICE_PLACEHOLDER = "GROUP";

/** 適格請求書発行事業者の登録番号の形（T＋13桁）か */
export function isInvoiceNo(v: string | null | undefined): boolean {
  return /^T\d{13}$/.test((v || "").trim());
}

/**
 * 会社名の表記ゆれを吸収する（法人格・空白・全角/半角・ダッシュの違いを無視）。
 *   "㈱AT-STYLE" / "株式会社AT-STYLE" / "ＡＴ－ＳＴＹＬＥ" → "AT-STYLE"
 */
export function normalizeCompanyName(name: string | null | undefined): string {
  return (name || "")
    .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
    .replace(/[\s　]/g, "")
    .replace(/(株式会社|有限会社|合同会社|合名会社|合資会社)/g, "")
    .replace(/(㈱|㈲|\(株\)|（株）|\(有\)|（有）)/g, "")
    .replace(/[－ー−‐–—―]/g, "-")
    .toUpperCase();
}

export type GroupInvoiceResolution = {
  /** 通知書に出す登録番号（T+13桁）。見つからなければ null */
  invoiceNo: string | null;
  /** どこから来た値か（設定画面 / マスタ / 無し） */
  source: "rule" | "master" | null;
  /** source==="master" のとき、番号の取得元になったマスタ行の氏名（設定画面の説明用） */
  fromDriverName?: string;
  /** マスタに候補が複数あり、どれが会社の番号か決められない（設定画面で指定してもらう） */
  ambiguous?: boolean;
  /** ambiguous のときの候補（「氏名: T…」の一覧。設定画面の説明用） */
  candidates?: { name: string; invoiceNo: string }[];
};

/** グループ宛通知書に出す登録番号を決める（①設定 → ②マスタ → ③無し） */
export function resolveGroupInvoiceNo(
  rule: Pick<GroupRule, "groupName" | "invoiceNo">,
  drivers: Driver[]
): GroupInvoiceResolution {
  const explicit = (rule.invoiceNo || "").trim();
  if (isInvoiceNo(explicit)) return { invoiceNo: explicit, source: "rule" };

  const target = normalizeCompanyName(rule.groupName);
  if (target) {
    const matches = drivers.filter(
      (d) => normalizeCompanyName(d.org) === target && isInvoiceNo(d.invoiceNo)
    );
    // 在籍者の行を優先（退職者の行に古い番号が残っていても、まず現役の行を使う）
    const active = matches.filter((d) => !isRetiredDriver(d));
    const pool = active.length > 0 ? active : matches;
    const distinct = [...new Set(pool.map((d) => (d.invoiceNo || "").trim()))];
    if (distinct.length > 1) {
      // 候補が割れている＝どれが「会社の」登録番号か決められない。推測で載せず設定画面で指定してもらう
      return {
        invoiceNo: null,
        source: null,
        ambiguous: true,
        candidates: pool.map((d) => ({ name: d.name, invoiceNo: (d.invoiceNo || "").trim() })),
      };
    }
    const picked = pool[0];
    if (picked) {
      return { invoiceNo: (picked.invoiceNo || "").trim(), source: "master", fromDriverName: picked.name };
    }
  }
  return { invoiceNo: null, source: null };
}

/** 合成ドライバーに入れる値（見つからなければダミー） */
export function groupDriverInvoiceNo(
  rule: Pick<GroupRule, "groupName" | "invoiceNo">,
  drivers: Driver[]
): string {
  return resolveGroupInvoiceNo(rule, drivers).invoiceNo ?? GROUP_INVOICE_PLACEHOLDER;
}
